import type { FieldInfo } from '@cognitoforms/types/server-types/model/field-info';
import { FieldType } from '@cognitoforms/types/server-types/model/field-type';
import { FieldSubType } from '@cognitoforms/types/server-types/model/field-sub-type';

export interface ExpressionBuilderPropertyMapping {
	Name: string;
	Id: string;
	Type: {
		Id: string;
		Name: string;
		DisplayName: string;
	};
	Choices?: string[];
	ValidateChoices: boolean;
	IncludeOnlyInScope: string;
	FieldType: string;
}

function appendFormatPropertyMappings(propertyMappings: ExpressionBuilderPropertyMapping[], parentFieldInfo: FieldInfo, names: string[]) {
	names.forEach(function (name) {
		const internalName = name.replace(/\s/g, '');
		if (parentFieldInfo.Format.indexOf(internalName) > -1)
			propertyMappings.push({
				Name: parentFieldInfo.Path + '.' + internalName,
				Id: parentFieldInfo.Id + '.' + internalName,
				Type: {
					Id: 'String',
					Name: 'String',
					DisplayName: 'String'
				},
				Choices: undefined,
				ValidateChoices: parentFieldInfo.ValidateChoices,
				IncludeOnlyInScope: parentFieldInfo.IncludeOnlyInScope,
				FieldType: 'Text'
			});
	});
}

/**
 * Similar to convertFieldInfosToTokens, but returns PropertyMappings instead of Tokens
 * Converts FieldInfo array into PropertyMappings for the Expression Builder
 * Adds Name/Address sub-fields and excludes non-ExpressionBuilderOnly Entry/Order fields
 */
export function convertFieldInfosToPropertyMappings(fieldInfos: FieldInfo[]) {
	if (!fieldInfos)
		return [];

	const propertyMappings: ExpressionBuilderPropertyMapping[] = [];

	fieldInfos.forEach(function (fieldInfo) {
		if (fieldInfo.Scope === 'Entry' || fieldInfo.Scope === 'Order')
			return;

		// Convert each FieldInfo to a PropertyMapping
		propertyMappings.push({
			Name: fieldInfo.Path,
			Id: fieldInfo.Id,
			Type: {
				Id: fieldInfo.PropertyType,
				Name: fieldInfo.PropertyType,
				DisplayName: fieldInfo.PropertyType
			},
			Choices: fieldInfo.Choices && fieldInfo.Choices.map(c => c.Label),
			IncludeOnlyInScope: fieldInfo.IncludeOnlyInScope,
			ValidateChoices: fieldInfo.ValidateChoices,
			FieldType: fieldInfo.FieldType
		});

		// Add property mappings based on format of Name field
		if (fieldInfo.FieldType === 'Name')
			appendFormatPropertyMappings(propertyMappings, fieldInfo, ['Prefix', 'First', 'Middle Initial', 'Middle', 'Last', 'Suffix']);

		// Add property mappings based on format of Address field
		else if (fieldInfo.FieldType === 'Address') {
			const formatFields = ['Line1', 'Line2', 'City', 'State', 'Postal Code'];
			if (fieldInfo.FieldSubType === 'InternationalAddress')
				formatFields.push('Country');

			appendFormatPropertyMappings(propertyMappings, fieldInfo, formatFields);
		}
	});

	return propertyMappings;
}

const transientProperties = [
	'Entry.Role',
	'Entry.Action',
	'Entry.User.Name',
	'Entry.User.FirstName',
	'Entry.User.LastName',
	'Entry.User.Email',
	'Form.Entry.Role',
	'Form.Entry.Action',
	'Form.Entry.User.Name',
	'Form.Entry.User.FirstName',
	'Form.Entry.User.LastName',
	'Form.Entry.User.Email'
];

type FieldInfoInternal = Partial<FieldInfo & { LookupHierarchy: string[] }>;

type ParentFieldInfo = FieldInfoInternal & Pick<FieldInfo, 'SourceFormId'> & { LookupHierarchy: string[] };

type ExpandLookupResult = {
	fieldInfos: FieldInfoInternal[];
	unprocessedLookupIndices: number[];
};

function isMultiSelectLookup(fieldInfo: FieldInfoInternal): boolean {
	return !!fieldInfo.FieldSubType && [FieldSubType.Checkboxes, FieldSubType.MultiSelectCards].includes(fieldInfo.FieldSubType);
}

/**
 * For a flat array of FieldInfos:
 * - resolves scope-specific properties of each FieldInfo with respect to the parentFieldInfo
 * - for each lookup fieldInfo encountered, resolves its lookup hierarchy and records its index
 * @param fieldInfos - array of FieldInfos to process
 * @param parentFieldInfo - the parent FieldInfo to resolve scope-specific properties against
 * @param expandMultiSelectLookups - whether to expand multi-select lookup fields
 * @returns the processed FieldInfos and the indices of encountered lookup FieldInfos
 */
function processFieldInfos(fieldInfos: FieldInfoInternal[], parentFieldInfo: ParentFieldInfo, expandMultiSelectLookups: boolean): ExpandLookupResult {
	const unprocessedLookupIndices: number[] = [];
	const expandedFieldInfos: FieldInfoInternal[] = [];

	fieldInfos.forEach(fieldInfo => {
		fieldInfo = { ...fieldInfo };

		// If parentFieldInfo is not the root form pseudo-fieldInfo (no Id), resolve the fieldInfo's scope-specific properties with respect to the parent fieldInfo
		if (parentFieldInfo.Id) {
			fieldInfo.Id = parentFieldInfo.Id + '.' + fieldInfo.Id;
			fieldInfo.ScopeId = parentFieldInfo.ScopeId ? parentFieldInfo.ScopeId + (fieldInfo.ScopeId ? '.' + fieldInfo.ScopeId : '') : fieldInfo.ScopeId;
			fieldInfo.Scope = parentFieldInfo.Scope + (fieldInfo.Scope ? ' ' + fieldInfo.Scope : '');
			fieldInfo.Path = parentFieldInfo.Path + '.' + fieldInfo.Path;
			fieldInfo.ExpressionBuilderOnly = fieldInfo.ExpressionBuilderOnly || parentFieldInfo.ExpressionBuilderOnly;

			if (fieldInfo.IncludeOnlyInScope)
				// Prepend parent path to IncludeOnlyInScope to make it relative to the parent fieldInfo
				fieldInfo.IncludeOnlyInScope = parentFieldInfo.Path + '.' + fieldInfo.IncludeOnlyInScope;
			else if (isMultiSelectLookup(parentFieldInfo))
				// If fieldInfo does not have an IncludeOnlyInScope, but the parent fieldInfo is a multiselect lookup, this fieldInfo should only be in parent's scope
				fieldInfo.IncludeOnlyInScope = parentFieldInfo.Path;
			else if (parentFieldInfo.IncludeOnlyInScope)
				// Otherwise, propagate parent's IncludeOnlyInScope if it has one
				fieldInfo.IncludeOnlyInScope = parentFieldInfo.IncludeOnlyInScope;
		}

		expandedFieldInfos.push(fieldInfo);

		// If fieldInfo is a lookup, resolve the hierarchy and record the current index for later expansion
		if (fieldInfo.FieldType === FieldType.Lookup && (expandMultiSelectLookups || !isMultiSelectLookup(fieldInfo))) {
			// Hierarchy is the parent fieldInfos's lookup hierarchy plus the parent field's source form id (the form these fields belong to)
			// eg. RootForm.LookupA will have hierarchy [RootForm]
			//     RootForm.LookupA.RootForm will have hierarchy [RootForm, LookupA]
			//	   RootForm.LookupA.RootForm.LookupX will have hierarchy [RootForm, LookupA, RootForm] and will not expand further due to circular reference
			fieldInfo.LookupHierarchy = parentFieldInfo.LookupHierarchy.concat([parentFieldInfo.SourceFormId]);
			unprocessedLookupIndices.push(expandedFieldInfos.length - 1);
		}
	});

	return {
		fieldInfos: expandedFieldInfos,
		unprocessedLookupIndices: unprocessedLookupIndices
	};
}

/**
 * Iteratively expands a dictionary mapping form IDs to arrays of FieldInfos into a single flat array of FieldInfos for a given root form.
 *
 * This function traverses and expands lookup fields within the provided fieldInfosByForm using an iterative breadth-first approach.
 * Lookup fieldInfos are iteratively expanded one level of depth at a time by fetching their source form's FieldInfos from the dictionary
 * and adding it to the running array, correctly resolving scope and handling circular references.
 * This process iterates until a stable state is reached where no unprocessed, expandable lookups remain or a maximum limit is hit.
 *
 * @param currentFormId Id of the root form whose FieldInfos are to be expanded
 * @param fieldInfosByForm Dictionary mapping FormId to a list of FieldInfos for every form referenced by any lookup
 * @param maxTokensLimit Maximum number of tokens allowed for expansion
 * @param expandMultiSelectLookups Whether multi-select lookup fields should also be expanded.  Corresponds to "includeLists" in server's FieldInfoGenerator (default: true)
 * @returns Flat array of FieldInfos representing the completely expanded FieldInfos list for the root form
 */
export function expandFieldInfos(currentFormId: string, fieldInfosByForm: Record<string, FieldInfo[]>, maxTokensLimit: number | null, expandMultiSelectLookups: boolean = true): FieldInfo[] {
	if (!fieldInfosByForm)
		return [];

	/* Maximum number of tokens allowed on the builder. If not overridden for an org, defaults to 50000 */
	const tokensLimit = maxTokensLimit || 50000;

	// In the worst case, every fieldInfo is a lookup and an index is added to unprocessedLookupIndices for each one
	// so the tokens limit should always be reached before this limit. This is just a safegaurd to prevent infinite loops
	// in case of unexpected issues with the fieldInfosByForm data
	const maxIterations = tokensLimit;

	// In the case of a new form, the current form id is null, and the server uses "0" as the key for the current form
	if (!currentFormId)
		currentFormId = '0';

	// Initialize with pseudo-fieldInfo for root form
	const parentFieldInfo: ParentFieldInfo = {
		SourceFormId: currentFormId,
		LookupHierarchy: []
	};

	const { fieldInfos, unprocessedLookupIndices } = processFieldInfos(fieldInfosByForm[currentFormId], parentFieldInfo, expandMultiSelectLookups);

	if (unprocessedLookupIndices.length === 0)
		return fieldInfos as FieldInfo[];

	// Shallow copy fieldInfosByForm
	fieldInfosByForm = { ...fieldInfosByForm };

	// Remove extended/transient fields from root form fieldInfos to avoid adding them again in case of circular references
	fieldInfosByForm[currentFormId] = fieldInfosByForm[currentFormId].filter(
		f => f.Scope !== 'Order' && f.Scope !== 'Entry' && !transientProperties.includes(f.Path)
	);

	// Iterate over unprocessed lookup indices and expand them
	let j = 0;
	while (j < unprocessedLookupIndices.length && fieldInfos.length < tokensLimit) {
		if (j >= maxIterations)
			// Should never happen
			throw new Error('Maximum iterations reached while expanding FieldInfos');

		const index = unprocessedLookupIndices[j];
		const fieldInfo = fieldInfos[index] as ParentFieldInfo;
		const fieldInfosForForm = fieldInfosByForm[fieldInfo.SourceFormId];

		// If no entry for this form is found in the provided dictionary, skip
		// If the current lookup hierarchy contains any form id more than once, this is a circular reference and should not expand further
		// eg. FormA -> FormA.FormB -> FormA.FormB.FormA, do not expand lookups FormA.FormB.FormA.X since FormA is already in the lookup hierarchy twice
		if (!fieldInfosForForm || (fieldInfo.LookupHierarchy && fieldInfo.LookupHierarchy.some((id, idx) => fieldInfo.LookupHierarchy.indexOf(id) !== idx))) {
			j++;
			continue;
		}

		// Retrieve FieldInfos for the lookup's source form, resolve their scope-specific properties, and identify any unprocessed lookups on this form
		const result = processFieldInfos(fieldInfosForForm, fieldInfo, expandMultiSelectLookups);
		const sourceFormFieldInfos = result.fieldInfos;
		const sourceUnprocessedLookupIndices = result.unprocessedLookupIndices;
		// Insert source form FieldInfos into fieldInfos array after the lookup FieldInfo
		fieldInfos.splice(index + 1, 0, ...sourceFormFieldInfos);

		// Adjust existing unprocessed lookup indices to account for newly inserted FieldInfos
		for (let k = j + 1; k < unprocessedLookupIndices.length; k++) {
			if (unprocessedLookupIndices[k] > index)
				unprocessedLookupIndices[k] += sourceFormFieldInfos.length;
		}

		// Add any new unprocessed lookup indices to the end of the list, to be processed after all current ones
		sourceUnprocessedLookupIndices.forEach(newIndex => {
			unprocessedLookupIndices.push(index + 1 + newIndex);
		});

		j++;
	}

	return fieldInfos as FieldInfo[];
}